/**
 * PlyrPlayer - Vanilla JS wrapper for Plyr
 * Supports MP4 and Vimeo with #FFE600 theme color
 */

class PlyrPlayer {
  constructor(container, options = {}) {
    this.container = container;
    this.url = options.url;
    this.poster = options.poster;
    this.type = options.type || 'mp4'; // 'mp4' or 'vimeo'
    this.player = null;
    this.element = null;

    if (this.url) {
      this.init();
    }
  }

  async init() {
    try {
      // Dynamic import Plyr
      const Plyr = (await import("plyr")).default;
      await import("plyr/dist/plyr.css");

      if (this.type === 'vimeo') {
        this.initVimeo(Plyr);
      } else {
        this.initVideo(Plyr);
      }
    } catch (error) {
      console.warn("Error initializing PlyrPlayer:", error);
      this.initFallback();
    }
  }

  initVideo(Plyr) {
    // Create video element for MP4
    this.element = document.createElement("video");
    this.element.className = "plyr-player";
    this.element.playsInline = true;
    this.element.controls = true;
    if (this.poster) {
      this.element.poster = this.poster;
    }

    const source = document.createElement("source");
    source.src = this.url;
    source.type = "video/mp4";
    this.element.appendChild(source);

    this.container.appendChild(this.element);
    this.initPlyr(Plyr);
  }

  initVimeo(Plyr) {
    // Create div for Vimeo embed
    this.element = document.createElement("div");
    this.element.className = "plyr-player plyr__video-embed";
    this.element.dataset.plyrProvider = "vimeo";
    this.element.dataset.plyrEmbedId = this.url;

    this.container.appendChild(this.element);
    this.initPlyr(Plyr);
  }

  initPlyr(Plyr) {
    this.player = new Plyr(this.element, {
      controls: [
        "play-large",
        "play",
        "progress",
        "current-time",
        "mute",
        "volume",
        "settings",
        "pip",
        "airplay",
        "fullscreen",
      ],
      settings: ["quality", "speed"],
      autoplay: false,
      muted: false,
      clickToPlay: true,
      hideControls: true,
      resetOnEnd: false,
      keyboard: { focused: true, global: false },
      tooltips: { controls: true, seek: true },
      fullscreen: { enabled: true, fallback: true, iosNative: true },
    });
  }

  initFallback() {
    if (this.type === 'vimeo') {
      // Fallback: native Vimeo iframe
      const iframe = document.createElement("iframe");
      iframe.src = `https://player.vimeo.com/video/${this.url}?title=0&byline=0&portrait=0`;
      iframe.className = "plyr-player";
      iframe.style.width = "100%";
      iframe.style.aspectRatio = "16/9";
      iframe.style.border = "none";
      iframe.allow = "autoplay; fullscreen; picture-in-picture";
      iframe.allowFullscreen = true;
      this.container.appendChild(iframe);
      this.element = iframe;
    } else {
      // Fallback: native video player
      this.element = document.createElement("video");
      this.element.className = "plyr-player";
      this.element.playsInline = true;
      this.element.controls = true;
      if (this.poster) {
        this.element.poster = this.poster;
      }

      const source = document.createElement("source");
      source.src = this.url;
      source.type = "video/mp4";
      this.element.appendChild(source);

      this.container.appendChild(this.element);
    }
  }

  destroy() {
    if (this.player) {
      try {
        this.player.destroy();
      } catch (e) {
        console.warn("Error destroying Plyr:", e);
      }
      this.player = null;
    }
    if (this.videoElement && this.videoElement.parentNode) {
      this.videoElement.parentNode.removeChild(this.videoElement);
    }
    this.videoElement = null;
  }

  play() {
    this.player?.play();
  }

  pause() {
    this.player?.pause();
  }

  get currentTime() {
    return this.player?.currentTime || 0;
  }

  set currentTime(time) {
    if (this.player) {
      this.player.currentTime = time;
    }
  }
}

export default PlyrPlayer;
